'use strict';

const _ = require('lodash');
const Promise = require('bluebird');
const Vue = require('vue/dist/vue.min.js');
const VueI18n = require('vue-i18n');
Vue.use(VueI18n);

const Api  = require('./Api');
const Chat = require('./Chat');
const Template = require('./Template');

const defaultLabels = require('../conversationTranscript/defaultLabels');
const defaultDateTimeFormats = require('../conversationTranscript/defaultTimeFormats');

/**
 * A helper utility
 */
class Helper {
    /**
     * Overrides the window setTimeout function using Web Workers
     * because in some browsers and background tabs, setTimeout is
     * assigned less browser resources, making it slow down.
     */
    static overrideSetTimeout() {
        if (global.window && global.window.Worker) {
            let timeoutId = 0;
            let timeouts = {};
            const timeoutWorker = function () {
                let timers = {};
                const fireTimeout = (id) => {
                    self.postMessage({ id });
                    delete timers[id];
                };
                self.onmessage = (evt) => {
                    let data = evt.data;
                    if (data.command === 'setTimeout') {
                        let time = parseInt(data.timeout || 0, 10);
                        let timer = setTimeout(fireTimeout.bind(null, data.id), time);
                        timers[data.id] = timer;
                    }
                };
            };

            let wfunction = global.window.URL.createObjectURL(new Blob(['('+timeoutWorker+')()']));
            let worker;

            try {
                worker = new global.window.Worker(wfunction);
            } catch (e) {
                // the context doesn't support workers from blob urls
                return;
            }

            worker.onmessage = (evt) => {
                let data = evt.data;
                let id = data.id;
                let fn = timeouts[id].fn;
                let args = timeouts[id].args;
                fn.apply(null, args);
                delete timeouts[id];
            };

            // this here refers to the Helper as a whole, so setTimeout
            // method will be available as another static method
            this.setTimeout = (fn, delay) => {
                let args = Array.prototype.slice.call(arguments, 2);
                timeoutId += 1;
                delay = delay || 0;
                let id = timeoutId;
                timeouts[id] = { fn, args };
                worker.postMessage({command: 'setTimeout', id: id, timeout: delay});
                return id;
            };
        }
    }

    /**
     * Downloads a media resource via an Api (AJAX) request
     * and creates the blob in client-side
     * @param {Object} media the media object, with all its properties, to be downloaded
     * @return {Promise<Boolean>} a Promise fulfilled with a boolean stating the success of the operation
     */
    static downloadMedia (media) {
        return Api.request(media.url, 'get', {}, { responseType: 'arraybuffer' })
            .then((res) => {
                try {
                    const blob = new Blob([res.data], { type: media.type || res.headers['content-type'] });
                    if (global.window.navigator.msSaveOrOpenBlob) {
                        global.window.navigator.msSaveBlob(blob, media.name);
                    } else {
                        const elem = global.window.document.createElement('a');
                        elem.href = global.window.URL.createObjectURL(blob);
                        elem.download = media.name;
                        // Add the target blank if the blob opens instead of download, avoid ios to compatibility problems
                        if (!Helper.isiOS()) {
                            elem.target = '_blank';
                        }
                        document.body.appendChild(elem);
                        elem.click();
                        document.body.removeChild(elem);
                    }
                } catch (err) {
                    return Promise.reject(false);
                }
            })
            .then(() => true)
            .catch(() => false);
    }

    /**
     * Update the element with the TTL provided every second. If the countdown gets to 0, remove the element.
     * @param  {HTMLElement}    inboxElement HTMLElement that will be removed when the countdown gets to 0
     * @param  {HTMLElement}    ttlElement HTMLElement where the TTL will be printed and updated
     * @param  {integer}        invitationExpiration UNIX time when the invitation expires
     * @param  {function}       callback to execute after the counter has reached 0
     */
    static controlInvitationTTL (inboxElement, ttlElement, invitationExpiration, callback) {
        const removeElementAndCallback = function (isExpiration) {
            inboxElement.remove();
            if (callback && _.isFunction(callback)) {
                callback(isExpiration);
            }
        };

        if (!(inboxElement instanceof HTMLElement)) {
            removeElementAndCallback();
            throw new Error('inboxElement is not instanceof HTMLElement');
        }
        if (!(ttlElement instanceof HTMLElement)) {
            removeElementAndCallback();
            throw new Error('ttlElement is not instanceof HTMLElement');
        }
        if (!document.body.contains(inboxElement)) {
            // inboxElement is not in document.body but it is an HTMLElement because no error was thrown
            removeElementAndCallback();
            return;  // The element no longer exists? Stop tracking it!
        }
        if (isNaN(invitationExpiration)) {
            removeElementAndCallback();
            return;
        }

        const ttl = invitationExpiration - Helper.getUnixTime();
        ttlElement.innerText = Template.label('__INVITATION_EXPIRING__', { ttl: ttl });

        if (ttl <= 0) {
            removeElementAndCallback(true);
        } else {
            Helper.runTimeout(() => {
                Helper.controlInvitationTTL(inboxElement, ttlElement, invitationExpiration, callback);
            }, 1000);
        }
    }

    /**
     * Returns a recalculated expiration time considering the browser time, the server time and the ping between them
     * @param {Date} serverExpirationTime A unix timestamp
     * @param {Date} timeDifferenceWithServer The difference in seconds between the local time and the server time
     * @param {Number} ping In miliseconds
     */
    static getBrowserExpirationTime (serverExpirationTime, timeDifferenceWithServer, ping) {
        const pingSeconds = Math.ceil(ping / 1000);
        const diff = timeDifferenceWithServer - pingSeconds;
        return serverExpirationTime + diff;
    }

    /**
     * Update the chat active timer element every second until the chat gets closed
     * @param  {Chat}           chat                Chat object
     * @param  {HTMLElement}    chatTimerElement    element where the timer will be printed and updated
     * @param  {integer}        acceptedTime        UNIX timestamp when the chat was accepted
     */
    static controlChatActiveTime (chat, chatTimerElement, acceptedTime) {
        // if closed
        if (!chat || chat.closed) {
            return;
        }

        if (chatTimerElement && (acceptedTime || acceptedTime === 0)) {
            // time in seconds since the chat was accepted for the first time
            var activeTime = Helper.getUnixTime() - acceptedTime;

            var date = new Date(null);
            date.setSeconds(activeTime);
            // get activeTime seconds in 'HH:MM:SS' format
            var strTime = date.toISOString().substr(11, 8);

            // set active time
            if (activeTime < 3600) {
                // remove hour digits if it's been less than an hour since it got accepted
                chatTimerElement.innerText = strTime.slice(3);
            } else {
                chatTimerElement.innerText = strTime;
            }

            Helper.runTimeout(() => {
                Helper.controlChatActiveTime(chat, chatTimerElement, acceptedTime);
            }, 1000);
        }
    }

    /**
    * Watches an input element for changes and manages and captures activity events
    * @param  {HTMLElement} input          HTMLElement instance of the chat input
    * @param  {Chat}        chat           Chat instance
    * @param  {integer}     intervalTime   Interval time to look for input changes
    * @param  {integer}     noChangeMax    Maximum number of intervals passed to consider that the user is not writing anymore
    * @private
    */
    static monitorUserActivity (input, chat, intervalTime, noChangeMax, errorHandler = null) {
        if (!(chat instanceof Chat)) {
            throw new Error('chat is not instanceof Chat');
        }
        if (!(input instanceof HTMLElement)) {
            throw new Error('input is not instanceof HTMLElement');
        }

        var lastValue = '', lastActivity = '', observeInterval = null, noChangeCounter = 0, focused = false;

        // check for changes in the input and fire the appropiate events
        const hasChange = () => {
            if (!document.body.contains(input)) {
                return;  // The element no longer exists? Stop tracking it!
            }
            const inputValue = input.value;
            let activity = lastActivity;
            clearTimeout(observeInterval);
            // if the last checked value is different from the current one, input has changed
            if (lastValue != inputValue) {
                // reset no change counter
                noChangeCounter = 0;
                // update last value to current
                lastValue = inputValue;
                if (inputValue !== '') {
                    activity = 'writing';
                }
            } else if (++noChangeCounter > noChangeMax) {
                // input hasn't changed, so increment no change counter and check if it surpassed the limit
                // if so, reset counter
                noChangeCounter = 0;
                // update activity
                activity = lastValue.trim() ? 'stop-writing' : 'not-writing';
            }
            // execute this function in the configured interval
            observeInterval = setTimeout(hasChange, intervalTime);
            // trigger the activity state
            triggerState(activity);
        };

        input.onkeyup = (event) => {
            if (event.key === 'Enter') {
                triggerState('not-writing');
            } else {
                hasChange();
            }
        };
        input.onfocus = () => {
            if (focused) {
                return;
            }
            focused = true;
            hasChange();
        };
        input.onblur = () => {
            focused = false;
            clearTimeout(observeInterval);
            const activity = lastValue.trim() ? 'stop-writing' : 'not-writing';
            triggerState(activity);
        };

        const triggerState = (activity) => {
            if (activity != lastActivity) {
                lastActivity = activity;
                Helper._sendActivity(activity, chat, errorHandler)
                    .then((result) => {
                        if (result == false) {
                            clearTimeout(observeInterval);
                        }
                    });
            }
        };
    }

    static _sendActivity (activity, chat, errorHandler) {
        if (chat.activity == activity) {
            Promise.resolve(true);
        }
        chat.activity = activity;
        return chat.sendUserActivity(activity)
            .then(() => true)
            .catch((err) => {
                if (errorHandler) {
                    errorHandler(err);
                }
                return false;
            });
    }

    static getUnixTime () {
        return Math.floor((new Date()).getTime() / 1000);
    }

    static runTimeout (callback, time) {
        const timeout = (this.setTimeout) ? this.setTimeout : setTimeout;
        timeout(callback, time);
    }

    static addScript (src) {
        return new Promise((resolve) => {
            const js = document.createElement('script');
            js.src = src;
            js.onload = resolve.bind(this);
            const hitch = document.getElementsByTagName('script')[0] || document.body;
            hitch.parentNode.insertBefore(js, hitch);
        });
    }

    static downloadBlob (fileName, blob) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        // Due to incompatibilites between iOS versions the target cannot be defined as _blank
        // link.target = '_blank';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    static downloadConversation (chatId, props = {}, requestData = {}) {
        Api.request('/chats/' + chatId + '/history', 'get', requestData)
            .then((res) => {

                // prepare data and properties
                const history = res.data.history;
                let createEvent;
                let joinCreatorEvent;
                const joinEvents = [];
                for (let i = 0; i < history.length; ++i) {
                    if (history[i].event === 'messages:new') {
                        history[i].data.message.message = Helper._getHistoryMessageText(history[i]);
                    } else if (history[i].event === 'chats:create') {
                        createEvent = history[i];
                    } else if (history[i].event === 'chats:join') {
                        joinEvents.push(history[i]);
                    }
                }
                for (let i = 0; i < joinEvents.length; i++) {
                    if (joinEvents[i].data.user.id === createEvent.trigger) {
                        joinCreatorEvent = joinEvents[i];
                        break;
                    }
                }

                // get locale
                let locale;
                if (createEvent.data.chat.lang) {
                    // set the locale to the chat language
                    locale = createEvent.data.chat.lang;
                } else if (props.labels) {
                    // if the chat has no language but there are custom labels set, get the first language specified
                    locale = Object.keys(props.labels)[0];
                }

                // create transcript in HTML
                const el = document.createElement('DIV');
                const i18n = new VueI18n({
                    locale: locale || 'en',
                    messages: _.merge(defaultLabels, props.labels),
                    dateTimeFormats: props.dateTimeFormats || defaultDateTimeFormats
                });

                let vm = null;
                if (props.template) {
                    vm = new Vue({
                        template: props.template,
                        data: () => {
                            return {
                                history,
                                creator: joinCreatorEvent.data.user,
                                ticketId: props.ticketId || 'chat transcript'
                            }
                        },
                        methods: props.methods || {},
                        i18n
                    });
                }
                else {
                    const renderTemplate = require('../conversationTranscript/defaultTemplate.js');
                    vm = new Vue({
                        render: h => renderTemplate(h, {
                            history,
                            creator: joinCreatorEvent.data.user,
                            ticketId: props.ticketId || 'chat transcript'
                        }, i18n)
                    });
                }
                vm.$mount(el);

                vm.$nextTick(() => {
                    // create PDF from HTML
                    const html2pdf = require('html2pdf.js');

                    const config = {
                        filename: (props.ticketId || 'chat transcript') + '.pdf',
                        html2canvas: { dpi: 250, scrollY: 0 },
                        pagebreak: { mode: 'avoid-all' },
                    };

                    Array.from(vm.$el.getElementsByTagName('img')).forEach(img => {
                        let link = document.createElement('a');
                        link.setAttribute("href", img.src);
                        link.innerHTML = img.src.substring(img.src.lastIndexOf('/')+1);
                        img.parentNode.replaceChild(link, img);
                    });

                    if (!Helper.isiOS()) {
                        html2pdf(vm.$el, config);
                    } else {
                        // open in new tab
                        html2pdf()
                            .set(config)
                            .from(vm.$el)
                            .toPdf()
                            .output('blob')
                            .then(function (blob) {
                                Helper.downloadBlob((props.ticketId || 'chat transcript') + '.pdf', blob);
                            });
                    }
                });
            });
    }

    /**
     * Given a history entry of type 'messages:new', get its text to show
     */
    static _getHistoryMessageText (historyEntry) {
        if (
            historyEntry.event && historyEntry.event === 'messages:new'
            && historyEntry.data && historyEntry.data.message
        ) {
            const msg = historyEntry.data.message;
            if (msg.type === 'text') {
                if (typeof msg.message !== 'string') {
                    try {
                        return JSON.stringify(msg.message);
                    } catch (e) {
                        return '';
                    }
                }
                return msg.message;
            } else if (msg.type === 'object') {
                let message = '';
                if (msg.message.message) {
                    message = msg.message.message;
                }
                if (msg.message.options) {
                    // get the different options labels as an array
                    const optionsLabels = msg.message.options.reduce((toReturn, option) => {
                        toReturn.push(option.label);
                        return toReturn;
                    }, []);
                    // enumerate labels separating them by commas
                    if (optionsLabels.length) {
                        var optionsText = '(' + optionsLabels.join(', ') + ')';
                        message += ' ' + optionsText;
                    }
                }
                return message;
            }
        }
        return '';
    }

    /**
     * Check if the user is using iOS (iPad/iPhone)
     * @return {Boolean}
     */
    static isiOS () {
        const w = global || window;
        return (w.navigator) ? (!!navigator.platform && /iPad|iPhone/.test(navigator.platform)) : false;
    }

    /**
     * Returns a promise with the timetable and holidays data
     * @return {Promise}
     */
    static getWorktimeTable(roomId, timezone) {
        if (!roomId) {
            throw new Error('A valid roomId must be provided');
        }

        let parameters = {};
        if (timezone) {
            parameters.timezone = timezone;
        }

        return Api.request('/rooms/' + roomId + '/timetable', 'get', parameters)
            .then((res) => {
                return res && res.data;
            });
    }

    /**
     * Returns a promise with a boolean as result
     * @return {Promise}
     */
    static isInWorktimeNow(roomId) {
        if (!roomId) {
            throw new Error('A valid roomId must be provided');
        }

        const timezone = 'UTC';
        return Helper.isWorktimeActive()
            .then((isActive) => {
                if (isActive) {
                    return Helper.getWorktimeTable(roomId, timezone);
                }

                return Promise.resolve(false);
            })
            .then((data) => {
                if (!data) {
                    return true;
                }

                let now = new Date(data.now);
                let isTodayHoliday = false;
                let isInWorktime = true;

                if (data.holidays && data.holidays.length > 0) {
                    // Format the day in the same format as the returned from API (integer in yyyymmdd format)
                    let today = parseInt(
                        now.getFullYear().toString()+
                        ("0" + (now.getMonth()+1).toString()).slice(-2)+
                        ("0" + now.getDate().toString()).slice(-2)
                    );
                    isTodayHoliday = data.holidays.includes(today);
                }

                if (data.timetable) {
                    let dayOfTheWeek = now.getDay() - 1;
                    if (dayOfTheWeek == -1) {
                        dayOfTheWeek = 7;
                    }

                    let timetable = data.timetable[dayOfTheWeek];
                    if (timetable && timetable.length > 0) {
                        isInWorktime = false;
                        let formattedTime =
                            ("0" + now.getUTCHours().toString()).slice(-2)
                            +':'
                            +("0" + now.getUTCMinutes().toString()).slice(-2);

                        timetable.forEach( timerange => {
                            if (timerange.from <= formattedTime && timerange.to >= formattedTime) {
                                isInWorktime = true;
                            }
                        });
                    }
                }

                return !isTodayHoliday && isInWorktime;
            });
    }

    /**
     * Returns a promise with the worktime settings flag
     * @return {Promise}
     */
    static isWorktimeActive() {
        return Api.request('/settings', 'get')
            .then((res) => {
                let settings = res && res.data && res.data.settings;
                if (settings) {
                    return settings.chat && settings.chat.isWorktimetableActive;
                }

                return false;
            });
    }
}

module.exports = Helper;



// WEBPACK FOOTER //
// ./src/Helper.js